import { GlassCard } from "@/components/ui/glass-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle, AlertCircle, Loader2, Terminal, ToggleLeft, ToggleRight } from "lucide-react";
import { type Device } from "@/hooks/useDevices";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface DeviceAuditLogProps {
  device: Device;
}

const DeviceAuditLog = ({ device }: DeviceAuditLogProps) => {
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success': return <CheckCircle className="h-4 w-4 text-primary" />;
      case 'error': case 'failed': return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'pending': return <Loader2 className="h-4 w-4 animate-spin text-accent-foreground" />;
      default: return <Terminal className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  const toggleRetry = async (deploymentId: string, currentValue: boolean) => {
    // No backend API for this yet
    toast.info('Retry toggle — Coming Soon');
  };

  return (
    <GlassCard variant="default" hover={false} className="p-6">
      <h2 className="text-xl font-semibold mb-4 text-foreground">Audit Log</h2>
      <ScrollArea className="h-[300px]">
        <div className="space-y-4">
          {device.script_deployments && device.script_deployments.length > 0 ? (
            device.script_deployments.map((deployment) => (
              <div key={deployment.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  {getStatusIcon(deployment.status)}
                  <div>
                    <p className="font-medium text-foreground">{deployment.configurations?.name || 'Unknown Script'}</p>
                    <p className="text-sm text-muted-foreground">Last run: {formatDate(deployment.last_run)}</p>
                    {deployment.exit_code !== null && (
                      <p className="text-sm text-muted-foreground">Exit code: {deployment.exit_code}</p>
                    )}
                    {deployment.output && (
                      <p className="text-sm text-muted-foreground mt-2 font-mono bg-card p-2 rounded">{deployment.output}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-sm px-3 py-1 rounded-full ${
                    deployment.status === 'success' ? 'bg-primary/10 text-primary' :
                    deployment.status === 'error' || deployment.status === 'failed' ? 'bg-destructive/10 text-destructive' :
                    'bg-accent text-accent-foreground'
                  }`}>{deployment.status}</span>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2" onClick={() => toggleRetry(deployment.id, deployment.retry_enabled || false)}>
                    {deployment.retry_enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                    <span className="text-sm">Retry {deployment.retry_enabled ? 'enabled' : 'disabled'}</span>
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-muted-foreground">No actions recorded yet</p>
          )}
        </div>
      </ScrollArea>
    </GlassCard>
  );
};

export default DeviceAuditLog;
